/** The main menu screen: Play, Replays, Settings, World atlas
 *  (docs/ui-decisions.md). */

import { For, Show, createResource, createSignal, onCleanup, type JSX } from "solid-js";
import { bytes } from "../ui/format";
import { type Me, setSession, whoAmI } from "../ui/session";
import { HowToPlay } from "./HowToPlay";
import { Settings } from "./Settings";

export type Section = "play" | "replays" | "learn" | "settings" | "account";
type Tab = "games" | "showcase" | "archive" | "season";

/** A stretch of the season between two snapshots (`/api/season/stretches`). */
interface Stretch {
  from: number;
  to: number;
  replay: string | null;
  making: boolean;
}

/** Season time at `tick` (10 a second): "Day 2 · 14:05". */
function seasonTime(tick: number): string {
  const s = Math.floor(tick / 10);
  const day = Math.floor(s / 86400) + 1;
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `Day ${day} · ${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

async function fetchStretches(): Promise<Stretch[]> {
  try {
    const r = await fetch("api/season/stretches", { cache: "no-store" });
    return r.ok ? ((await r.json()) as Stretch[]) : [];
  } catch {
    return [];
  }
}

/** A season's own replays are listed under Season, not Showcase. */
const SEASON_REPLAY = /^season-\d+-\d+-\d+\.ferro$/;
/** A replay in an index (the archive's also carry title and sim version). */
type Item = { name: string; bytes: number; title?: string; version?: string };

/** What the menu asks of the app. */
export interface MenuActions {
  /** Start a battle: the live world or a skirmish (query, as in `?live&...`). */
  play(query: string): void;
  replay(path: string): void;
  file(file: File): void;
  atlas(): void;
  resume(): void;
}

/** The menu's state, set from outside (the app). */
export const [section, setSection] = createSignal<Section>("play");
export const [resumable, setResumable] = createSignal(false);
export const [liveError, setLiveError] = createSignal("");
export const [loadError, setLoadError] = createSignal("");
/** Bumped to re-fetch the replay list (each time the menu opens). */
export const [listVersion, setListVersion] = createSignal(0);
/** Bumped to re-check who's signed in. */
export const [accountVersion, setAccountVersion] = createSignal(0);
/** A season faction's name (they're numbered until players name them). */

/** Games played on the server: live-SEED-UNIXTIME, skirmish-LEVEL-SEED-UNIXTIME. */
const GAME = /^(live|skirmish-(beginner|easy|normal|hard))-\d+-(\d+)(-\d+)?\.ferro$/;
const stamp = (name: string) => Number(name.match(GAME)![3]);
const when = (name: string) => new Date(stamp(name) * 1000).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
const kind = (name: string) => (name.startsWith("live") ? "Live battle" : `Skirmish (${name.match(GAME)![2]})`);
/** A replay's name as the menu lists it ("Skirmish (easy), Oct 13, 03:56"). */
export const replayLabel = (name: string) => {
  const file = name.replace(/^.*\//, "");
  return GAME.test(file) ? `${kind(file)}, ${when(file)}` : file.replace(/\.ferro$/, "");
};

/** An archived replay's title, with its file name where that says more. */
const archived = (it: Item) => {
  const base = it.name.replace(/\.ferro$/, "");
  return it.title && it.title !== base ? `${it.title} (${base})` : base;
};

async function fetchList(url: string): Promise<Item[]> {
  try {
    const res = await fetch(url, { cache: "no-store" });
    return res.ok ? ((await res.json()) as Item[]) : [];
  } catch {
    return [];
  }
}

export function Menu(props: { actions: MenuActions }): JSX.Element {
  const a = props.actions;
  const [me] = createResource(accountVersion, () => whoAmI());
  const nav = (s: Section, label: string) => (
    <button aria-current={section() === s ? "page" : undefined} onClick={() => setSection(s)}>
      {label}
    </button>
  );
  return (
    <>
      <nav class="menu-nav" aria-label="Main menu">
        <div class="brand big">
          <svg viewBox="0 0 32 32" width="30" height="30" aria-hidden="true">
            <polygon points="16,2 28,9 28,23 16,30 4,23 4,9" fill="currentColor" />
            <polygon points="16,9 22,12.5 22,19.5 16,23 10,19.5 10,12.5" fill="var(--bg)" />
          </svg>
          <span>FERRO</span>
        </div>
        <Show when={resumable()}>
          <button id="resume-btn" class="primary" onClick={() => a.resume()}>
            Resume
          </button>
        </Show>
        {nav("play", "Play")}
        {nav("replays", "Replays")}
        {nav("learn", "How to play")}
        {nav("settings", "Settings")}
        <button onClick={() => a.atlas()}>World atlas</button>
      </nav>
      <Profile me={me()} open={() => setSection("account")} />
      <main class="menu-body">
        <Show when={section() === "play"}>
          <section class="menu-section">
            <Play play={a.play} me={me()} />
          </section>
        </Show>
        <Show when={section() === "account"}>
          <section class="menu-section">
            <Account me={me()} />
          </section>
        </Show>
        <Show when={section() === "replays"}>
          <section class="menu-section start-replays">
            <Replays replay={a.replay} file={a.file} />
          </section>
        </Show>
        <Show when={section() === "learn"}>
          <section class="menu-section">
            <HowToPlay />
          </section>
        </Show>
        <Show when={section() === "settings"}>
          <section class="menu-section settings">
            <Settings />
          </section>
        </Show>
      </main>
    </>
  );
}

/** The commander, in the corner (the game convention: a profile button).
 *  Signed out, it starts signing in; signed in, it opens their account. */
function Profile(props: { me: Me | null | undefined; open: () => void }): JSX.Element {
  return (
    <Show
      when={props.me}
      fallback={
        <a class="profile" href="auth/discord" aria-label="Sign in with Discord">
          <svg viewBox="0 0 32 32" width="22" height="22" aria-hidden="true">
            <circle cx="16" cy="12" r="6" fill="currentColor" />
            <path d="M5 29c1.5-7 6-10 11-10s9.5 3 11 10z" fill="currentColor" />
          </svg>
          <span>Sign in</span>
        </a>
      }
    >
      {(m) => (
        <button class="profile" onClick={() => props.open()} aria-label="Your account">
          <Show when={m().avatar}>{(src) => <img src={src()} alt="" width="32" height="32" />}</Show>
          <span>{m().name}</span>
        </button>
      )}
    </Show>
  );
}

function Account(props: { me: Me | null | undefined }): JSX.Element {
  const signOut = () => {
    setSession(null);
    setAccountVersion((v) => v + 1);
    setSection("play");
  };
  return (
    <>
      <h1>Account</h1>
      <Show when={props.me} fallback={<a class="button" href="auth/discord">Sign in with Discord</a>}>
        {(m) => (
          <div class="account-card">
            <Show when={m().avatar}>{(src) => <img src={src()} alt="" width="72" height="72" />}</Show>
            <div>
              <h2>{m().name}</h2>
              <button class="button" onClick={signOut}>
                Sign out
              </button>
            </div>
          </div>
        )}
      </Show>
    </>
  );
}

/** The species a new player may choose (ferro-sim's `species::SPECIES`):
 *  each trades something for something. Health, speed and growth relative
 *  to a baseline body; sight in hexes. */
const SPECIES = [
  { name: "Lupine", hp: 100, speed: 115, sight: 1, growth: 20 },
  { name: "Ursine", hp: 160, speed: 80, sight: 0, growth: 12 },
  { name: "Vulpine", hp: 85, speed: 110, sight: 2, growth: 22 },
  { name: "Murine", hp: 60, speed: 105, sight: -1, growth: 45 },
];

function Play(props: { play: (query: string) => void; me: Me | null | undefined }): JSX.Element {
  const [fog, setFog] = createSignal(false);
  const [species, setSpecies] = createSignal(SPECIES[0].name);
  const [askSignIn, setAskSignIn] = createSignal(false);
  const join = () => {
    if (!props.me) {
      setAskSignIn(true);
      return;
    }
    // A first join carries the species chosen (the server takes it once).
    props.play(`live&side=${props.me.side ?? 0}${props.me.side == null ? `&species=${species()}` : ""}`);
  };
  // A trait as a bar, against the strongest of the four.
  const bar = (label: string, value: number, max: number, min = 0) => (
    <span class="trait">
      <span class="muted">{label}</span>
      <span class="trait-bar">
        <span style={{ width: `${Math.max(4, ((value - min) / (max - min)) * 100)}%` }} />
      </span>
    </span>
  );
  return (
    <>
      <h1>Play</h1>
      <div class="play-choice season">
        <h2>Season</h2>
        <Show when={props.me?.side == null}>
          <div class="species" role="radiogroup" aria-label="Species">
            <For each={SPECIES}>
              {(s) => (
                <button class="species-card" role="radio" aria-checked={species() === s.name} onClick={() => setSpecies(s.name)}>
                  <span class="species-name">{s.name}</span>
                  {bar("Health", s.hp, 160)}
                  {bar("Speed", s.speed, 115, 60)}
                  {bar("Sight", s.sight, 2, -2)}
                  {bar("Growth", s.growth, 45)}
                </button>
              )}
            </For>
          </div>
        </Show>
        <div class="start-actions">
          <button class="button primary" onClick={join}>
            {props.me?.side != null ? "Resume" : "Join the season"}
          </button>
          <button class="button" onClick={() => props.play("live&side=watch")}>
            Watch
          </button>
        </div>
      </div>
      <div class="play-choice">
        <h2>Skirmish</h2>
        <div class="play-setting">
          <span>Fog of war</span>
          <div class="segmented" role="radiogroup" aria-label="Fog of war">
            <button role="radio" aria-checked={!fog()} onClick={() => setFog(false)}>
              Off
            </button>
            <button role="radio" aria-checked={fog()} onClick={() => setFog(true)}>
              On
            </button>
          </div>
        </div>
        <div class="start-actions">
          <For each={["beginner", "easy", "normal", "hard"]}>
            {(level) => (
              <button class="button" onClick={() => props.play(`live&skirmish=${level}${fog() ? "&fog=1" : ""}`)}>
                {level[0].toUpperCase() + level.slice(1)}
              </button>
            )}
          </For>
        </div>
      </div>
      <Show when={liveError()}>
        <p class="error">{liveError()}</p>
      </Show>
      <Show when={askSignIn()}>
        <div class="dialog-backdrop" onClick={() => setAskSignIn(false)}>
          <div class="dialog" role="dialog" aria-modal="true" aria-labelledby="sign-in-title" onClick={(e) => e.stopPropagation()}>
            <h2 id="sign-in-title">Sign in to join</h2>
            <p>The season keeps your faction between visits: sign in with Discord to hold one.</p>
            <div class="start-actions">
              <a class="button primary" href="auth/discord">
                Sign in with Discord
              </a>
              <button class="button" onClick={() => setAskSignIn(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </Show>
    </>
  );
}

function Replays(props: { replay: (path: string) => void; file: (f: File) => void }): JSX.Element {
  const saved = (() => {
    try {
      return localStorage.getItem("ferro-replay-tab") as Tab | null;
    } catch {
      return null;
    }
  })();
  const [tab, setTab] = createSignal<Tab>(saved ?? "games");
  const [items] = createResource(listVersion, () => fetchList("replays/index.json"));
  const [archive] = createResource(() => tab() === "archive", (on) => (on ? fetchList("replays/archive/index.json") : Promise.resolve([] as Item[])));
  const [stretches, { refetch: refetchStretches }] = createResource(() => tab() === "season", (on) => (on ? fetchStretches() : Promise.resolve([] as Stretch[])));
  // While the server makes a replay, look again every few seconds.
  const poll = setInterval(() => {
    if (tab() === "season" && (stretches() ?? []).some((s) => s.making)) void refetchStretches();
  }, 3000);
  onCleanup(() => clearInterval(poll));
  const make = async (s: Stretch) => {
    try {
      await fetch(`api/season/replay?from=${s.from}`, { cache: "no-store" });
    } catch {
      /* the list shows what happened */
    }
    void refetchStretches();
  };
  const choose = (t: Tab) => {
    setTab(t);
    try {
      localStorage.setItem("ferro-replay-tab", t);
    } catch {
      /* fine */
    }
  };
  // Rows: (path, label, tag, bytes), for the chosen tab.
  const rows = (): [string, string, string, number][] => {
    const list = items() ?? [];
    const t = tab() === "games" && !list.some((it) => GAME.test(it.name)) ? "showcase" : tab();
    if (t === "archive") {
      const major = (it: Item) => Number((it.version ?? "0").split(".")[0]);
      return [...(archive() ?? [])]
        .sort((a, b) => major(b) - major(a) || a.name.localeCompare(b.name))
        .map((it) => [`archive/${it.name}`, archived(it), it.version ? `v${it.version.split(".")[0]}` : "", it.bytes]);
    }
    if (t === "games") {
      return list
        .filter((it) => GAME.test(it.name))
        .sort((a, b) => stamp(b.name) - stamp(a.name))
        .map((it) => [it.name, `${kind(it.name)}, ${when(it.name)}`, "", it.bytes]);
    }
    return list.filter((it) => !GAME.test(it.name) && !SEASON_REPLAY.test(it.name)).map((it) => [it.name, it.name.replace(/\.ferro$/, ""), "", it.bytes]);
  };
  return (
    <>
      <div class="start-replays-head">
        <h1>Replays</h1>
        <div class="segmented" role="radiogroup" aria-label="Replay list">
          <For each={[["games", "Your games"], ["season", "Season"], ["showcase", "Showcase"], ["archive", "Archive"]] as [Tab, string][]}>
            {([t, label]) => (
              <button role="radio" aria-checked={tab() === t} onClick={() => choose(t)}>
                {label}
              </button>
            )}
          </For>
        </div>
      </div>
      <Show when={tab() === "season"}>
        <div class="replay-list">
          <For each={[...(stretches() ?? [])].reverse()} fallback={<p class="empty">None</p>}>
            {(s) => (
              <button disabled={s.making} onClick={() => (s.replay ? props.replay(s.replay) : void make(s))}>
                <span class="label">
                  {seasonTime(s.from)} – {seasonTime(s.to).split(" · ")[1]}
                </span>
                <span class="size">{s.replay ? "Watch" : s.making ? "Making…" : "Make replay"}</span>
              </button>
            )}
          </For>
        </div>
      </Show>
      <div class="replay-list" classList={{ hidden: tab() === "season" }}>
        <For each={rows()} fallback={<p class="empty">{tab() === "games" ? "No games yet" : "None"}</p>}>
          {([path, label, tag, size]) => (
            <button onClick={() => props.replay(path)}>
              <span class="label">{label}</span>
              <span class="size">{[tag, bytes(size)].filter(Boolean).join(" · ")}</span>
            </button>
          )}
        </For>
      </div>
      <div class="start-foot">
        <label class="file">
          <input
            type="file"
            accept=".ferro"
            onChange={(e) => {
              const f = e.currentTarget.files?.[0];
              if (f) props.file(f);
              e.currentTarget.value = "";
            }}
          />
          <span>Open a .ferro file</span>
        </label>
        <span class="muted small drop-hint">or drop one here</span>
      </div>
      <Show when={loadError()}>
        <p class="error">{loadError()}</p>
      </Show>
    </>
  );
}
